import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Page from "@/components/Page";
import Button from "@/components/Button";
import { GUIDELINES } from "@/common/constants";
import { getPartnerConfig } from "@/common/partnerConfig";
import { trackEvent } from "@/lib/amplitude";
import ProgressIndicator from "./shared/ProgressIndicator";

export default function OnboardingVariantA() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current step from URL (default to 0)
  const currentStep = parseInt(searchParams.get("step") || "0");
  const [startTime] = useState(Date.now());

  // Get partner-specific configuration
  const placeRef = window.localStorage.getItem("placeRef");
  const partnerConfig = getPartnerConfig(placeRef);
  const translationSuffix = partnerConfig?.guidelines.customTranslationSuffix ?? "";

  // Get guidelines (partner-aware)
  const guidelines = partnerConfig?.guidelines.showSubjectGuideline
    ? GUIDELINES
    : GUIDELINES.slice(0, 3);

  // Total steps: 1 welcome + N guidelines
  const totalSteps = guidelines.length + 1;

  // Track onboarding start
  useEffect(() => {
    trackEvent("onboarding_started", {
      variant: "A",
      experiment: "onboarding_flow_test",
      placeRef,
      partner: partnerConfig?.id,
      guidelinesCount: guidelines.length,
      source: placeRef ? "qr_code" : "direct",
    });

    return () => {
      if (currentStep < totalSteps - 1) {
        trackEvent("onboarding_abandoned", {
          variant: "A",
          experiment: "onboarding_flow_test",
          placeRef,
          partner: partnerConfig?.id,
          lastStep: currentStep,
          timeSpent: Date.now() - startTime,
        });
      }
    };
  }, []);

  // Track step views
  useEffect(() => {
    trackEvent("onboarding_step_viewed", {
      variant: "A",
      experiment: "onboarding_flow_test",
      placeRef,
      partner: partnerConfig?.id,
      stepNumber: currentStep,
      stepType: currentStep === 0 ? "welcome" : "guideline",
      guidelineName: currentStep > 0 ? guidelines[currentStep - 1].titleKey : null,
    });
  }, [currentStep]);

  const goToNextStep = () => {
    const nextStep = currentStep + 1;

    trackEvent("onboarding_step_advanced", {
      variant: "A",
      experiment: "onboarding_flow_test",
      placeRef,
      partner: partnerConfig?.id,
      fromStep: currentStep,
      toStep: nextStep,
    });

    if (nextStep >= totalSteps) {
      // Completed all steps
      trackEvent("onboarding_completed", {
        variant: "A",
        experiment: "onboarding_flow_test",
        placeRef,
        partner: partnerConfig?.id,
        guidelinesCount: guidelines.length,
        timeSpent: Date.now() - startTime,
      });
      navigate("/upload");
    } else {
      setSearchParams({ step: nextStep.toString() });
      // Scroll to top on step change
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goToPreviousStep = () => {
    const prevStep = currentStep - 1;

    trackEvent("onboarding_step_back", {
      variant: "A",
      experiment: "onboarding_flow_test",
      placeRef,
      partner: partnerConfig?.id,
      fromStep: currentStep,
      toStep: prevStep,
    });

    setSearchParams({ step: prevStep.toString() });
    // Scroll to top on step change
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Render welcome page
  if (currentStep === 0) {
    return (
      <Page
        lngChange
        title={t("guidelines.title")}
        action={<Button onPress={goToNextStep}>{t("guidelines.next")}</Button>}
      >
        <div className="flex grow flex-col justify-between">
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-y-3 py-8 text-center">
            <p className="text-3xl font-bold">{t("guidelines.welcome.title")}</p>
            <p className="text-base text-foreground/80">
              {t("guidelines.welcome.description")}
            </p>
          </div>

          <ProgressIndicator current={0} total={totalSteps} />
        </div>
      </Page>
    );
  }

  // Render guideline page
  const guidelineIndex = currentStep - 1;
  const guideline = guidelines[guidelineIndex];
  const { Icon, titleKey, labelKey } = guideline;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <Page
      lngChange
      title={t("guidelines.title")}
      action={
        <div className="flex w-full gap-3">
          {currentStep > 0 && (
            <Button onPress={goToPreviousStep} variant="secondary">
              {t("guidelines.back")}
            </Button>
          )}
          <Button onPress={goToNextStep} className="flex-1 text-nowrap">
            {isLastStep ? t("guidelines.buttonText") : t("guidelines.next")}
          </Button>
        </div>
      }
    >
      <div className="flex grow flex-col justify-between">
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-y-3 py-8">
          <h2 className="mb-3 text-center text-3xl font-bold text-foreground">
            {t(titleKey)}
          </h2>
          <div className="mb-6 rounded-3xl bg-medium-gray/30 p-6 shadow-[inset_0_0px_10px_rgba(255,255,255,0.3),inset_0_-2px_4px_rgba(0,0,0,0.2)]">
            <Icon size={80} className="fill-foreground" />
          </div>

          <p className="max-w-md text-center text-sm text-foreground/80">
            {t(`${labelKey}${translationSuffix}`, {
              defaultValue: t(labelKey),
            })}
          </p>
        </div>
      </div>

      <ProgressIndicator current={currentStep} total={totalSteps} />
    </Page>
  );
}
